import {
    BlurEvent,
    Button, ClickEvent, Component, DataDisplayEvent, DataSourceMode, Image, Label, Layout, List,
    LookupModelSelectionEvent, Panel, Table, TableRow, TableRowDisplayEvent, TableRowMode, Textbox
} from "@mcleod/components";
import {
    Api, Currency, DisplayType, FieldUpdateEvent, HorizontalAlignment, ModelRow, RowUpdateEvent, StringUtil
} from "@mcleod/core";
import { CommonTooltips, UserQuickInfo } from "@mcleod/common";
import { LMEGeneralSettings } from "@mcleod/general";
import { CarrierAssignmentStepper } from "@mcleod/powerbroker";
import { CarrierOfferInfo } from "./QuickOfferSlideout";
import { DispatchUtil } from "./DispatchUtil";
import { OfferTabEventAction, OfferTabType } from "./MovementOfferTabs";
import { WaterfallOffers } from "./WaterfallOffers";
import { MovementOffers } from "./MovementOffers";
import { Movement } from "./Movement";

export enum OfferSource {
    BLAST = "BLAST",
    QUICKOFFER = "QUICKOFFER",
    WATERFALL = "WATERFALL"
}

export class OfferUtil {
    private carrierQualificationErrors = new Map<string, string>();
    private layout: MovementOffers | WaterfallOffers;
    private movementID: string;
    private moveRow: ModelRow;
    private mainLayout: Movement;

    constructor(movementID: string, moveRow: ModelRow, layout: MovementOffers | WaterfallOffers, mainLayout?: Movement) {
        this.movementID = movementID;
        this.moveRow = moveRow;
        this.layout = layout;
        this.mainLayout = mainLayout;
        this.addTableListeners();
    }

    addTableListeners() {
        this.layout.tableOffers.addContentsChangedListener(() => {
            this.layout.onTabEvent({ type: this.getTabType(), action: OfferTabEventAction.CONTENTS_CHANGED });
        });
    }

    getTabType(): OfferTabType {
        return this.layout instanceof WaterfallOffers ? OfferTabType.WATERFALL : OfferTabType.PENDING;
    }

    setOfferStatusLabel(status: string, statusPanel: Panel) {
        enum Styles {
            GREENFILL,
            GREEN,
            ORANGE,
            RED
        }

        let style: Styles;
        let labelText: string;
        switch (status) {
            case "Z":
                style = Styles.ORANGE;
                labelText = "PENDING ACCEPT";
                break;
            case "A":
                style = Styles.GREENFILL;
                labelText = "ACCEPTED";
                break;
            case "O":
                style = Styles.ORANGE;
                labelText = "COUNTERED";
                break;
            case "D":
                style = Styles.RED;
                labelText = "Declined";
                break;
            case "E":
                style = Styles.RED;
                labelText = "Expired";
                break;
            case "P":
                style = Styles.GREEN;
                labelText = "Pending";
                break;
            case "S":
                style = Styles.RED;
                labelText = "Skipped";
                break;
            case "C":
                style = Styles.RED;
                labelText = "Cancelled";
                break;
            case "F":
                style = Styles.GREEN;
                labelText = "Electronic Offer";
                break;
            default:
                style = Styles.GREEN;
                labelText = "Initial Offer"
                break;
        }

        const color = style === Styles.ORANGE ? "warning.reverse" :
            style === Styles.RED ? "error" :
                style === Styles.GREENFILL ? "success.reverse" : "success";
        const borderColor = style === Styles.ORANGE ? "warning" :
            style === Styles.RED ? "error" : "success";
        const backgroundColor = style === Styles.ORANGE ? "warning" :
            style === Styles.GREENFILL ? "success" : null;

        statusPanel.borderColor = borderColor;
        statusPanel.backgroundColor = backgroundColor;

        const statusLabel = statusPanel.findComponentById("labelStatus") as Label;
        if (statusLabel) {
            statusLabel.text = labelText;
            statusLabel.color = color;
        }

        const caret = statusPanel.findComponentById("imageCaret");
        if (caret) {
            caret.color = color;
        }
    }

    validateCarrierForDispatch(tableRow: TableRow) {
        this.getValidationErrorMessage(tableRow).then(result => {
            if (result != null) {
                const modelRow = new ModelRow("", false, result.data[0]);
                modelRow.set("payee_id", tableRow?.data?.get("payee_id"));
                tableRow.forEveryChildComponent((comp: Component) => {
                    this.setQualificationCallback(comp, modelRow);
                });
            }
        });
    }

    // Can't see where this is used...
    private async getCarrierValidationQuickInfoLayout(carrierId: string, warningList?: List, errorList?: List): Promise<Layout> {
        let layout = null;
        if (carrierId != null) {
            layout = await Layout.getLoadedLayout("lme/powerbroker/CarrierValidationWarningQuickInfo");
            const modelRow = new ModelRow(layout.mainDataSource.url, false, { id: carrierId });
            layout.mainDataSource.setRowsAndMode(DataSourceMode.NONE, [modelRow])
            layout["labelWarningMessage"].visible = false;

            const items = [];
            if (warningList != null) {
                if (warningList) {
                    for (const warning of Object.values(warningList)) {
                        const label = new Label({ text: warning });
                        items.push(label);
                    }
                }
            }
            if (errorList != null) {
                if (errorList) {
                    for (const error of Object.values(errorList)) {
                        let label = null;

                        if (error.details != null)
                            label = new Label({ text: error.message + "\n\t" + error.details.join("\n\t") });
                        else
                            label = new Label({ text: error.message });

                        items.push(label);
                    }
                }
            }
            layout["errorList"].items = items;
        }
        return layout;
    }

    getValidationErrorMessage(tableRow: TableRow): Promise<any> {
        const carrierId = tableRow?.data?.get("payee_id");
        const contactName = tableRow?.data?.get("contact_name");
        const contactEmail = tableRow?.data?.get("email");
        if (carrierId == null) return Promise.resolve(null);
        if (!this.carrierQualificationErrors.has(carrierId)) {
            const validateError = tableRow?.data?.get("carrier_validation_error");
            if (validateError != null && tableRow?.data.getChangedData()["payee_id"] == null) {
                this.carrierQualificationErrors.set(carrierId, validateError);
                return Promise.resolve(validateError);
            } else if (this.movementID != null) {
                return new Promise((resolve) => {
                    Api.search("lme/powerbroker/carrier-dispatch-validator",
                        { "movement_id": this.movementID, "payee_id": carrierId, "contact_name": contactName, "contact_email": contactEmail}).then(result => {
                        resolve(result);
                    });
                });
            }
        }
        return Promise.resolve(this.carrierQualificationErrors.get(carrierId));
    }

    iccNumberRequired(textbox: Textbox, offerRow: ModelRow) {
        textbox.required = StringUtil.isEmptyString(offerRow.get("dot_number"));
        textbox.validate(false, false);
    }

    setTooltipContent(tableRow: TableRow) {
        const offerDate = tableRow.findComponentById("labelOfferDate") as Label;
        if (offerDate) {
            offerDate.tooltipDynamicContent = async () => {
                return await this.makeUserQuickInfoLayout(tableRow.data);
            }
        }
        const tbAmount = tableRow.findComponentById("textboxAmount");
        if (tbAmount) {
            if (tableRow.data.get("carrier_rate_lane_id") != null) {
                CommonTooltips.setTooltipContentLayout(
                    tbAmount,
                    tableRow.data.get("carrier_rate_lane_id"),
                    "lme/dispatch/rates/CarrierLaneRateQuickInfo"
                );
            } else if (tableRow.data.get("source") !== undefined) {
                tbAmount.tooltipDynamicContent = () => { return this.makeSourceTooltipContent(tableRow.data.get("source")) };
                const panelStatus = tableRow.findComponentById("panelStatus") as Panel;
                panelStatus.tooltipDynamicContent = () => { return this.makeSourceTooltipContent(tableRow.data.get("source")) };
            }
        }

        const tbCounter = tableRow.findComponentById("textboxCounterOffer");
        if (tbCounter) {
            tbCounter.tooltipDynamicContent = () => { return this.makeCounterDateTooltipContent(tableRow.data) };
        }
    }

    makeSourceTooltipContent(source: String): Panel {
        const panel = new Panel({ padding: 0, margin: 0 });
        panel.add(new Label({ text: "Offer Source", fontSize: "small", fontBold: true }));
        panel.add(new Label({ text: `     ${source}` }));
        return panel;
    }

    makeRateTooltipContent(tableRow: TableRow, component: Component) {
        if (!tableRow.data.isNull("carrier_rate") && tableRow.data.getCurrency("carrier_rate").getAmount() == tableRow.data.getCurrency("amount")?.getAmount()) {
            CommonTooltips.setTooltipContentLayout(
                component,
                tableRow.data.get("carrier_rate_lane_id", null),
                "lme/dispatch/rates/CarrierLaneRateQuickInfo"
            );
        }
        return;
    }

    private async makeUserQuickInfoLayout(modelRow: ModelRow): Promise<Layout> {
        const layout = await Layout.getLoadedLayout("common/UserQuickInfo") as UserQuickInfo;
        if (modelRow.get("entered_user_id") instanceof ModelRow) {
            layout.mainDataSource.data = [modelRow.get("entered_user_id")];
            layout.mainDataSource.rowIndex = 0;
        } else {
            await layout.mainDataSource.search({ id: modelRow.get("entered_user_id") });
        }
        layout.textboxId.caption = "Entered By";
        return layout;
    }

    makeCounterDateTooltipContent(modelRow: ModelRow): Panel {
        if (modelRow.isNull("counter_offer_n")) return null;
        const panel = new Panel({ padding: 0, margin: 0 });
        panel.add(new Label({
            text: "Counter Offer Received",
            fontSize: "large",
            fontBold: true,
            align: HorizontalAlignment.CENTER,
            fillRow: true
        }));
        panel.add(new Label({
            text: modelRow.get("counter_offer_d"),
            displayType: DisplayType.DATETIME,
            align: HorizontalAlignment.CENTER,
            fillRow: true
        }));
        return panel;
    }

    tableOffersOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow: TableRow = event.target as TableRow;
        const textboxCarrierName = tableRow.findComponentById("textboxCarrierName") as Textbox;
        if (textboxCarrierName) {
            textboxCarrierName.addLookupModelSelectionListener((event: LookupModelSelectionEvent) => this.carrierSelected(event));
        }
        const textboxContactName = tableRow.findComponentById("textboxContactName") as Textbox;
        if (textboxContactName) {
            textboxContactName.addLookupModelSelectionListener((event: LookupModelSelectionEvent) => this.contactSelected(event));
        }
        const textboxContactEmail = tableRow.findComponentById("textboxContactEmail") as Textbox;
        if (textboxContactEmail) {
            textboxContactEmail.addBlurListener((event: BlurEvent) => this.emailChanged(event));
        }

        this.syncAssignButton(tableRow);
        this.validateCarrierForDispatch(tableRow);
        this.addAfterPostListener(tableRow.data, tableRow.table);
        const tbIccNum = tableRow.findComponentById("textboxIccNumber") as Textbox;
        if (tbIccNum) {
            this.iccNumberRequired(tbIccNum, tableRow.data as ModelRow);
        }
        const panelStatus = tableRow.findComponentById("panelStatus") as Panel;
        if (panelStatus) {
            this.setOfferStatusLabel(tableRow.data.get("status"), panelStatus);
        }
        this.setTooltipContent(tableRow);
    }

    setQualificationCallback(comp: Component, modelRow: ModelRow) {
        if (comp.id == null) return;
        switch (comp.id) {
            case "imageError":
                if (modelRow.getString("qualification_status") === "Disqualified") {
                    comp.visible = true;
                    CommonTooltips.setTooltipContentLayout(comp, modelRow, "lme/dispatch/CarrierQualificationDetailsQuickInfo");
                }
                else {
                    comp.visible = false;
                }
                break;
            case "imageWarning":
                if (modelRow.getString("qualification_status") === "Warning") {
                    comp.visible = true;
                    CommonTooltips.setTooltipContentLayout(comp, modelRow, "lme/dispatch/CarrierQualificationDetailsQuickInfo");
                }
                else {
                    comp.visible = false;
                }
                break;
        }
    }

    private carrierSelected(event: LookupModelSelectionEvent) {
        if (event.hasSelection() !== true)
            return;
        const selectedRow = event.selection;
        const tableRow = TableRow.getContainingTableRow(event.target);
        Api.search("lme/dispatch/calculate-override-pay", {
            "movement_id": this.moveRow.get("id"),
            "order_id": this.moveRow.get("orders.id"),
            "payee_id": selectedRow.get("id"),
            "auto_rate": LMEGeneralSettings.getSingleton().getDispatchControl().rate_carrier == "Y",
            "calculate_fuel_surcharge": true
        }).then(response => {
            if (response.data[0]?.carrier_rate_lane_id) {
                //Had to create new currency for each because of passing by reference issues
                const amount = new Currency(response.data[0]?.total_pay);
                const carrier_rate = new Currency(response.data[0]?.total_pay);
                tableRow.data.set("amount", amount);
                tableRow.data.set("carrier_rate", carrier_rate);
                tableRow.data.set("carrier_rate_id", response.data[0]?.carrier_rate_id);
                tableRow.data.set("carrier_rate_lane_id", response.data[0]?.carrier_rate_lane_id);
            } else {
                tableRow.data.set("carrier_rate", null);
            }
            let name = selectedRow.get("contact.name");
            let email = selectedRow.get("contact.email");
            let phone = selectedRow.get("contact.phone", selectedRow.get("contact.mobile_phone", selectedRow.get("phone_number")));
            if (!StringUtil.isEmptyString(response.data[0]?.carrier_contact)) {
                name = response.data[0]["carrier_contact"];
                email = response.data[0]["carrier_email"];
                phone = response.data[0]["carrier_phone"];
            }


            const numberType = tableRow.data.get("number_type");
            const number = (() => {
                switch (numberType) {
                    case "M":
                        return selectedRow.get("icc_number");
                    case "D":
                        return selectedRow.get("dot_number");
                    default:
                        return selectedRow.get("intrastate_code");
                }
            })();

            tableRow.data.setValues({
                "carrier_name": selectedRow.get("name"),
                "payee_id": selectedRow.get("id"),
                "icc_number": selectedRow.get("icc_number"),
                "dot_number": selectedRow.get("dot_number"),
                "intrastate_code": selectedRow.get("intrastate_code"),
                "number": number,
                "contact_name": name,
                "contact_phone": phone,
                "email": email,
                "carrier_ranking": selectedRow.get("carrier_ranking"),
                "rating_star_count": selectedRow.get("rating_star_count"),
            });
            tableRow.data.setLookupModelData("contact_name", { "name": name });
            tableRow.data.setLookupModelData("carrier_name", { "name": selectedRow.get("name") });
            const textboxIccNumber = tableRow.findComponentById("textboxIccNumber") as Textbox;
            if (textboxIccNumber) {
                this.iccNumberRequired(textboxIccNumber, tableRow.data);
            }
            this.validateCarrierForDispatch(tableRow);
            tableRow.displayComponentValues();
        });
    }

    private contactSelected(event: LookupModelSelectionEvent) {
        if (event.hasSelection() === true) {
            const selectedRow = event.selection;
            const tableRow = TableRow.getContainingTableRow(event.target);
            tableRow.data.setValues({
                "contact_name": selectedRow.get("name"),
                "contact_phone": selectedRow.get("phone"),
                "email": selectedRow.get("email")
            });
            this.validateCarrierForDispatch(tableRow);
            tableRow.displayComponentValues();
        }
    }

    emailChanged(event: BlurEvent) {
        if (event.changedWhileFocused) {
            const textbox = event.target as Textbox;
            const tableRow = TableRow.getContainingTableRow(textbox);
            this.validateCarrierForDispatch(tableRow);
        }
    }

    syncAssignButton(tableRow: TableRow) {
        const modelRow = tableRow?.data as ModelRow;
        const buttonAssignCarrier = tableRow.findComponentById("buttonAssignCarrier") as Button;
        if (buttonAssignCarrier == null) return;
        if (tableRow.mode === TableRowMode.QUICK_ADD) {
            buttonAssignCarrier.visible = false;
        } else {
            if (modelRow["payeeListener"] == null) {
                const payeeListener = (event: FieldUpdateEvent) => {
                    if (event.fieldName == "payee_id")
                        this.enableAssignButton(buttonAssignCarrier);
                }
                modelRow["payeeListener"] = payeeListener;
                modelRow.addAfterFieldUpdateListener(payeeListener);
            }
            this.enableAssignButton(buttonAssignCarrier);
        }
    }

    enableAssignButton(buttonAssignCarrier: Button) {
        if (buttonAssignCarrier) {
            buttonAssignCarrier.disabledTooltip = null;
            DispatchUtil.isAssignable(this.moveRow, { btnAssign: buttonAssignCarrier });
        }
    }

    addAfterPostListener(modelRow: ModelRow, tableOffers: Table) {
        if (modelRow != null && modelRow["_rowPostListenersAdded"] != true) {
            // tableOffers.persistChangesImmediately == true, so an AfterPostListener is required
            // to display/hide carrier validation warning after row is posted. This is fired when
            // adding / modify a quickAdd TableRow or when saving data in the edit slideout
            modelRow.addAfterPostListener((event: RowUpdateEvent) => {
                const tableRow = tableOffers.rows.find(tableRow => tableRow.data.get("id") == event.row.get("id"));
                this.validateCarrierForDispatch(tableRow);
                return Promise.resolve();
            });
            modelRow["_rowPostListenersAdded"] = true;
        }
    }

    showTooltipOnDataDisplay(event: DataDisplayEvent) {
        const textbox = event.target as Textbox;
        const modelRow = textbox.getRelevantModelRow();
        textbox.quickInfoOverrideKeyValue = modelRow.get("payee_id");
    }

    assignCarrierEvent(event: ClickEvent) {
        const button = event.target as Button;
        const tableRow = TableRow.getContainingTableRow(button);

        if (tableRow.table.persistChangesImmediately)
            tableRow.saveChanges(() => this.showAssignCarrierSlideout(tableRow, button))
        else
            this.showAssignCarrierSlideout(tableRow, button);
    }

    showAssignCarrierSlideout(tableRow: TableRow, button: Button = tableRow.findComponentById("buttonAssignCarrier") as Button) {
        const modelRow = tableRow.data as ModelRow;
        if (button == null || modelRow == null) return;
        const carrierOfferInfo: CarrierOfferInfo = {
            payee_id: modelRow.get("payee_id", null),
            carrier_name: modelRow.get("carrier_name", ""),
            icc_number: modelRow.get("icc_number", null),
            dot_number: modelRow.get("dot_number", null),
            carrier_contact: modelRow.get("contact_name", ""),
            carrier_phone: modelRow.get("contact_phone", ""),
            carrier_email: modelRow.get("email", ""),
            dispatch_offer: modelRow.getCurrency("counter_offer", modelRow.getCurrency("amount")),
            counter_offer: modelRow.getCurrency("counter_offer", null),
            carrier_rate: modelRow.getCurrency("carrier_rate", null),
            carrier_empty_from_city_id: modelRow.get("tractor_city_id", "")
        }

        CarrierAssignmentStepper.showAsSlideout({
            movementId: this.moveRow.get("id"),
            orderId: this.moveRow.get("orders.id"),
            carrierAssignmentButton: button,
            carrierOfferInfo,
            onClose: async (canceled: boolean) => {
                if (!canceled) {
                    if (!(this.layout instanceof WaterfallOffers)) {
                        await this.doAfterOfferAssignment();
                        this.mainLayout?.mainDataSource.search({ id: this.moveRow.get("id") });
                    }
                }
            }
        });
    }

    async doAfterOfferAssignment() {
        if (this.layout != null) {
            await this.layout.sourceMovement?.search({ id: this.movementID })
            this.layout.tableOffers.rows.forEach(tableRow => {
                const buttonAssignCarrier = tableRow.findComponentById("buttonAssignCarrier") as Button;
                this.enableAssignButton(buttonAssignCarrier);
            });
        }
    }

    sortOffers(source?: OfferSource) {
        let offerTypeWeights: Object;
        if (source == OfferSource.WATERFALL) {
            offerTypeWeights = {
                "I": 1, // Initial
                "O": 2, // Countered
                "P": 3, // Pending
                "D": 4, // Declined
                "E": 5, // Expired
                "S": 6  // Skipped
            }
        } else {
            offerTypeWeights = {
                "Z": 1 // Pending Accept
            }
        }
        this.layout.mainDataSource.data = this.layout.mainDataSource.data.filter((row) => row.get("status") != "C").sort((row1, row2) => {
            if (row1.get("status") == row2.get("status")) {
                return row1.get("offer_date") > row2.get("offer_date") ? -1 : 1;
            }
            return (offerTypeWeights[row1.get("status")] ?? 100) - (offerTypeWeights[row2.get("status")] ?? 100);
        });
        this.layout.mainDataSource.displayDataInBoundComponents()
    }
}
